import React, { useState, useEffect } from 'react';
import {
    Box,
    TableCell,
    TableRow,
    LinearProgress,
    Alert,
    Snackbar,
    Typography,
    Chip,
    Switch
} from '@mui/material';
import { AppButton } from '../../../shared/components/AppButton';
import { AppTable } from '../../../shared/components/AppTable';
import { AppTablePagination } from '../../../shared/components/AppTablePagination';
import { AppSearch } from '../../../shared/components/AppSearch';
import {
    Sync as SyncIcon,
} from '@mui/icons-material';
import { getCategoryValues, syncCategoryValues, getCategoryValueSyncStatus, toggleTopRated } from '../../../shared/services/categoryValueService';
import { Loader } from '../../../shared/components/Loader';
import { usePermission } from '../../../shared/contexts/PermissionContext';
import { SyncProgressBanner, type SyncProgressDetails } from '../../../shared/components/SyncProgressBanner';
import { CategoryValueItem } from '../../../shared/types/categoryValue';

export const CategoryValue: React.FC = () => {
    const { hasPermission, isSuperAdmin } = usePermission();
    const [allCategoryValues, setAllCategoryValues] = useState<CategoryValueItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [syncing, setSyncing] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [searchInput, setSearchInput] = useState('');
    const [syncDetails, setSyncDetails] = useState<SyncProgressDetails | null>(null);
    const [togglingId, setTogglingId] = useState<number | string | null>(null);

    useEffect(() => {
        loadCategoryValues();
    }, []);

    const loadCategoryValues = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await getCategoryValues({
                page: 1,
                limit: 1000
            });

            if (response.success && response.data) {
                setAllCategoryValues(response.data.items || []);
            } else {
                setAllCategoryValues([]);
            }
        } catch (err: any) {
            console.error('Error loading category values:', err);
            setError(err.response?.data?.message || 'Failed to load category values');
            setAllCategoryValues([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredCategoryValues = React.useMemo(() => {
        if (!searchInput.trim()) return allCategoryValues;
        const q = searchInput.trim().toLowerCase();
        return allCategoryValues.filter(item =>
            (item.categoryValueName?.toLowerCase() || '').includes(q) ||
            (item.catName?.toLowerCase() || '').includes(q) ||
            (item.categoryValueId?.toString().toLowerCase() || '').includes(q)
        );
    }, [allCategoryValues, searchInput]);

    const paginatedCategoryValues = React.useMemo(() => {
        const start = currentPage * pageSize;
        return filteredCategoryValues.slice(start, start + pageSize);
    }, [filteredCategoryValues, currentPage, pageSize]);

    const handleSync = async () => {
        let pollInterval: any;
        try {
            setSyncing(true);
            setError('');
            setSyncDetails(null);

            await syncCategoryValues();

            const checkStatus = async () => {
                try {
                    const statusRes = await getCategoryValueSyncStatus();
                    if (statusRes.success) {
                        setSyncDetails(statusRes.data as any);

                        if (statusRes.data.isRunning) {
                            await loadCategoryValues();
                        } else {
                            if (pollInterval) clearInterval(pollInterval);
                            setSyncing(false);
                            if (statusRes.data.lastError) {
                                setError(statusRes.data.lastError);
                            } else {
                                setSuccess('Category values synced successfully');
                            }
                            await loadCategoryValues();
                        }
                    }
                } catch (err) {
                    console.error('Polling error:', err);
                    if (pollInterval) clearInterval(pollInterval);
                    setSyncing(false);
                }
            };

            await checkStatus();
            pollInterval = setInterval(checkStatus, 3000);

        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to start sync');
            setSyncing(false);
        }
    };

    const handleToggleTopRated = async (item: CategoryValueItem) => {
        const id = item.id || item.categoryValueId.toString();
        const originalStatus = !!item.isTopRated;

        try {
            // Optimistic update
            setAllCategoryValues(prev => prev.map(cv =>
                cv.categoryValueId === item.categoryValueId
                    ? { ...cv, isTopRated: !originalStatus }
                    : cv
            ));

            const response = await toggleTopRated(id);
            if (response.success) {
                // Confirm final state
                setAllCategoryValues(prev => prev.map(cv =>
                    cv.categoryValueId === item.categoryValueId
                        ? { ...cv, isTopRated: response.data?.isTopRated ?? !originalStatus }
                        : cv
                ));
                setSuccess(response.message || 'Top rated status updated');
            } else {
                // Rollback
                setAllCategoryValues(prev => prev.map(cv =>
                    cv.categoryValueId === item.categoryValueId
                        ? { ...cv, isTopRated: originalStatus }
                        : cv
                ));
                setError(response.message || 'Failed to update status');
            }
        } catch (err: any) {
            // Rollback on catch
            setAllCategoryValues(prev => prev.map(cv =>
                cv.categoryValueId === item.categoryValueId
                    ? { ...cv, isTopRated: originalStatus }
                    : cv
            ));
            setError(err.response?.data?.message || 'Failed to update top rated status');
        }
    };

    const handleCloseSnackbar = () => {
        setError('');
        setSuccess('');
    };

    const handleSearchChange = (value: string) => {
        setSearchInput(value);
        setCurrentPage(0);
    };

    const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
        setCurrentPage(newPage - 1);
    };

    return (
        <div className="flex flex-col flex-auto min-w-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row flex-0 sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b bg-card dark:bg-transparent">
                <div className="flex-1 min-w-0">
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                        Category Values
                    </h2>
                </div>
                <div className="flex items-center gap-4 mt-4 sm:mt-0 flex-wrap">
                    <AppSearch
                        value={searchInput}
                        onChange={handleSearchChange}
                        placeholder="Search category values"
                        className="w-full sm:w-64"
                        disabled={loading}
                    />
                    {(isSuperAdmin() || hasPermission('categoryValue.sync')) && (
                        <AppButton
                            variant="primary"
                            startIcon={<SyncIcon />}
                            onClick={handleSync}
                            disabled={syncing || loading}
                            loading={syncing}
                        >
                            {syncing ? 'Syncing...' : 'Sync Category Values'}
                        </AppButton>
                    )}
                </div>
            </div>

            {/* Content Area */}
            <div className="flex flex-auto p-6 sm:p-10">
                <div className="flex flex-col flex-auto min-w-0 w-full">
                    {/* Sync Progress Banner */}
                    <SyncProgressBanner syncing={syncing} details={syncDetails} />

                    {loading && allCategoryValues.length > 0 && <LinearProgress className="mb-4" />}
                    {loading && allCategoryValues.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Loader />
                        </Box>
                    ) : filteredCategoryValues.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Alert severity="info">
                                {searchInput.trim() ? 'No category values match your search.' : 'No data found.'}
                            </Alert>
                        </Box>
                    ) : (
                        <>
                            <AppTable
                                head={
                                    <TableRow className="bg-gray-100">
                                        <TableCell className="!font-semibold !text-gray-700">Value Name</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Category (CatName)</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Top Rated</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Status</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Sync TS</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Last Updated</TableCell>
                                    </TableRow>
                                }
                                isEmpty={filteredCategoryValues.length === 0}
                                emptyColSpan={6}
                                emptyMessage={searchInput.trim() ? 'No category values match your search.' : 'No data in this page.'}
                            >
                                {paginatedCategoryValues.map((item) => (
                                    <TableRow key={item.id || item.categoryValueId} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                                {item.categoryValueName}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                ID: {item.categoryValueId}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={item.catName || 'N/A'}
                                                size="small"
                                                variant="outlined"
                                                color="primary"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Switch
                                                checked={!!item.isTopRated}
                                                onChange={() => handleToggleTopRated(item)}
                                                disabled={togglingId === item.categoryValueId}
                                                color="primary"
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={item.catStatus === 'Y' ? 'Active' : 'Inactive'}
                                                size="small"
                                                color={item.catStatus === 'Y' ? 'success' : 'error'}
                                                variant="filled"
                                            />
                                        </TableCell>
                                        <TableCell>{item.syncTs}</TableCell>
                                        <TableCell>
                                            {item.updatedAt ? new Date(item.updatedAt).toLocaleString() : '-'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </AppTable>

                            <AppTablePagination
                                totalRows={filteredCategoryValues.length}
                                page={currentPage}
                                pageSize={pageSize}
                                onPageChange={handlePageChange}
                                onPageSizeChange={(size) => {
                                    setPageSize(size);
                                    setCurrentPage(0);
                                }}
                            />
                        </>
                    )}
                </div>
            </div>

            {/* Snackbar for notifications */}
            <Snackbar
                open={!!error || !!success}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {error ? (
                    <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                        {error}
                    </Alert>
                ) : (
                    <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                        {success}
                    </Alert>
                )}
            </Snackbar>
        </div>
    );
};
